import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { routeToPage } from 'support/helpers/navigation/navigation';
import { MessageV2DTO, ReceiverEnvelopeState, TransactionsRoutes, TransactionState } from 'support/types';
import { Alert } from 'components/Display/Alert/Alert';

import { determineDuplicateAndChangeDocumentState } from './helpers';

type DuplicateAndChangeDocumentBannerProps = {
  message: MessageV2DTO | undefined;
};

export function DuplicateAndChangeDocumentBanner({ message }: DuplicateAndChangeDocumentBannerProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const alertConfig = useMemo(() => {
    switch (determineDuplicateAndChangeDocumentState(message)) {
      case 'duplicate':
        return {
          text: t('transactions:show.sections.alert.duplicateAndChangeDocument.duplicate.text'),
          buttonLabel: t('transactions:show.sections.alert.duplicateAndChangeDocument.duplicate.buttonLabel'),
          analyticsId: 'duplicate-alert-view-original',
          routeUrl: routeToPage(TransactionsRoutes.viewMessage, { id: message!.replaces_message!.id }),
        };

      case 'replacedBy':
        return {
          text:
            message!.replaced_by_message!.state === TransactionState.pending_review
              ? t('transactions:show.sections.alert.duplicateAndChangeDocument.replacedBy.pendingReview.text')
              : t('transactions:show.sections.alert.duplicateAndChangeDocument.replacedBy.default.text'),
          buttonLabel: t('transactions:show.sections.alert.duplicateAndChangeDocument.replacedBy.buttonLabel'),
          analyticsId: 'replaced-by-alert-view-latest',
          routeUrl: routeToPage(TransactionsRoutes.viewMessage, { id: message!.replaced_by_message!.id }),
        };

      case 'replaces':
        return {
          text:
            message!.receiver_envelope?.state === ReceiverEnvelopeState.manuallyDelivered
              ? t('transactions:show.sections.alert.duplicateAndChangeDocument.replaces.manuallyDelivered.text')
              : t('transactions:show.sections.alert.duplicateAndChangeDocument.replaces.default.text'),
          buttonLabel: t('transactions:show.sections.alert.duplicateAndChangeDocument.replaces.buttonLabel'),
          analyticsId: 'replaces-alert-view-previous',
          routeUrl: routeToPage(TransactionsRoutes.viewMessage, { id: message!.replaces_message!.id }),
        };

      default:
        return null;
    }
  }, [message, t]);

  if (!alertConfig) {
    return null;
  }

  return (
    <Alert
      severity="neutral"
      withoutIcon
      fullWidth
      actions={{
        primary: {
          onClick: () => navigate(alertConfig.routeUrl),
          children: alertConfig.buttonLabel,
          analyticsId: alertConfig.analyticsId,
        },
      }}
    >
      {alertConfig.text}
    </Alert>
  );
}
